import { useEffect } from "react";
import { useShipmentFiltersContext } from "../context/ShipmentFiltersContext";
import { useTableControl } from "../context/TableControlContext";

/**
 * Hook that syncs filter values from ShipmentFiltersContext to TableControlContext.filters
 * This is needed because TableFilterPopover reads from TableControlContext.filters to show
 * the filter badge, but OrdersFilter (which normally does the sync) only renders when
 * the popover is open.
 * 
 * This hook should be used in a component that's always mounted (like ShipmentsTable).
 */
export function useFilterSync() {
  const { setFilters } = useTableControl();
  const {
    selectedCustomer,
    trackingNumbersFilter,
    displayIdsFilter,
    selectedOrderLabelIds,
    selectedDriverLabelIds,
    selectedEndCustomerLabelIds,
    selectedCourierBrandIds,
    selectedAcquisitionTypes,
    selectedRecurrenceTypes,
    selectedPickupZoneIds,
    selectedDropoffZoneIds,
    selectedPackageTypeIds,
    selectedOrderVehicleTypeIds,
    selectedDriverVehicleTypeIds,
    selectedServiceTypeIds,
    minWeightInPounds,
    maxWeightInPounds,
    selectedRouteTemplateIds,
    shipmentDateRangeStart,
    shipmentDateRangeEnd,
    completedDateRangeStart,
    completedDateRangeEnd,
  } = useShipmentFiltersContext();

  const shipmentDateRangeValue =
    shipmentDateRangeStart || shipmentDateRangeEnd
      ? { start: shipmentDateRangeStart, end: shipmentDateRangeEnd }
      : undefined;
  const completedDateRangeValue =
    completedDateRangeStart || completedDateRangeEnd
      ? { start: completedDateRangeStart, end: completedDateRangeEnd }
      : undefined;

  // Sync all filter values to TableControlContext.filters
  // IDs must match OrdersFilter.tsx for consistent sort order
  useEffect(() => {
    setFilters([
      { id: 0, type: "endCustomerId", value: selectedCustomer?.id },
      { id: 1, type: "trackingNumbersFilter", value: trackingNumbersFilter },
      { id: 2, type: "displayIdsFilter", value: displayIdsFilter },
      { id: 10, type: "driverLabelIds", value: selectedDriverLabelIds },
      { id: 11, type: "orderLabelIds", value: selectedOrderLabelIds },
      { id: 13, type: "endCustomerLabelIds", value: selectedEndCustomerLabelIds },
      { id: 14, type: "courierBrandIds", value: selectedCourierBrandIds },
      { id: 15, type: "pickupZoneIds", value: selectedPickupZoneIds },
      { id: 16, type: "dropoffZoneIds", value: selectedDropoffZoneIds },
      { id: 17, type: "packageTypeIds", value: selectedPackageTypeIds },
      { id: 18, type: "orderVehicleTypeIds", value: selectedOrderVehicleTypeIds },
      { id: 19, type: "driverVehicleTypeIds", value: selectedDriverVehicleTypeIds },
      { id: 20, type: "serviceTypeIds", value: selectedServiceTypeIds },
      { id: 21, type: "minWeightInPounds", value: minWeightInPounds },
      { id: 22, type: "maxWeightInPounds", value: maxWeightInPounds },
      { id: 23, type: "acquisitionTypes", value: selectedAcquisitionTypes },
      { id: 24, type: "recurrenceTypes", value: selectedRecurrenceTypes },
      { id: 25, type: "shipmentDateRange", value: shipmentDateRangeValue },
      { id: 26, type: "completedDateRange", value: completedDateRangeValue },
      { id: 27, type: "routeTemplateIds", value: selectedRouteTemplateIds },
    ]);
  }, [
    selectedCustomer,
    trackingNumbersFilter,
    displayIdsFilter,
    selectedOrderLabelIds,
    selectedDriverLabelIds,
    selectedPickupZoneIds,
    selectedDropoffZoneIds,
    selectedPackageTypeIds,
    selectedOrderVehicleTypeIds,
    selectedDriverVehicleTypeIds,
    selectedServiceTypeIds,
    selectedEndCustomerLabelIds,
    selectedCourierBrandIds,
    selectedAcquisitionTypes,
    selectedRecurrenceTypes,
    minWeightInPounds,
    maxWeightInPounds,
    selectedRouteTemplateIds,
    shipmentDateRangeStart,
    shipmentDateRangeEnd,
    completedDateRangeStart,
    completedDateRangeEnd,
    setFilters,
  ]);
}
